import React, { useState, useEffect } from 'react';
import { AlertCircle, Check, Loader, Save, X } from 'lucide-react';
import { api } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const ChannelConfigsManagement = ({
  icon: Icon,
  title,
  subtitle,
  apiPrefix,
  tokenLabel,
  tokenFieldName,
  recipientLabel,
  recipientFieldName
}) => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { confirm, confirmDialog } = useConfirmDialog();
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  const initialFormData = {
    name: '',
    enabled: false,
    [tokenFieldName]: '',
    [recipientFieldName]: ''
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      loadConfigs(selectedStore.id);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const loadStores = async () => {
    setError('');
    try {
      const response = await api.get('/admin/stores');
      setStores(response.data);
      if (response.data.length > 0) {
        setSelectedStore(response.data[0]);
      }
    } catch (err) {
      console.error('Error loading stores:', err);
      setError('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async (storeId) => {
    setError('');
    setConfigs([]);
    try {
      const response = await api.get(`${apiPrefix}/list`, { params: { storeIdFk: storeId } });
      setConfigs(response.data);
    } catch (err) {
      console.error(`Error loading ${title} configs:`, err);
      setError(`Failed to load ${title} configurations`);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Configuration name is required');
      return false;
    }
    if (formData.enabled) {
      const existingToken = editingConfig && (editingConfig.hasToken || editingConfig.hasBotToken);
      if (!existingToken && !formData[tokenFieldName].trim()) {
        setError(`${tokenLabel} is required when the channel is enabled`);
        return false;
      }
      if (!formData[recipientFieldName].trim()) {
        setError(`${recipientLabel} is required when the channel is enabled`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedStore || !validateForm()) return;

    setSaving(true);
    setError('');
    setSuccess('');

    const payload = {
      storeIdFk: selectedStore.id,
      name: formData.name.trim(),
      enabled: formData.enabled,
      [tokenFieldName]: formData[tokenFieldName].trim() || null,
      [recipientFieldName]: formData[recipientFieldName].trim() || null
    };

    try {
      if (editingConfig) {
        await api.put(`${apiPrefix}/${editingConfig.id}`, payload);
      } else {
        await api.post(apiPrefix, payload);
      }
      await loadConfigs(selectedStore.id);
      setShowForm(false);
      setEditingConfig(null);
      setFormData(initialFormData);
      setSuccess(`Configuration saved successfully`);
    } catch (err) {
      console.error(`Error saving ${title} config:`, err);
      setError(err.response?.data?.error || err.response?.data?.message || `Failed to save ${title} configuration`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const config = configs.find((c) => c.id === id);
    const storeName = selectedStore ? (selectedStore.name || selectedStore.storeId) : null;
    const confirmed = await confirm({
      title: `Delete ${title} configuration?`,
      message: `The ${title} configuration${config?.name ? ` "${config.name}"` : ''}${storeName ? ` for store "${storeName}"` : ''} will be permanently deleted.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    setError('');
    try {
      await api.delete(`${apiPrefix}/${id}`);
      await loadConfigs(selectedStore.id);
      setSuccess(`${title} configuration deleted`);
    } catch (err) {
      console.error(`Error deleting ${title} config:`, err);
      setError(err.response?.data?.error || err.response?.data?.message || `Failed to delete ${title} configuration`);
    }
  };

  const handleToggleEnabled = async (config) => {
    const nextEnabled = !config.enabled;
    if (nextEnabled && (!hasTokenFlag(config) || !config[recipientFieldName])) {
      setError(`${tokenLabel} and ${recipientLabel} must be set before enabling this configuration`);
      return;
    }
    setError('');
    try {
      await api.put(`${apiPrefix}/${config.id}`, {
        storeIdFk: config.storeIdFk,
        name: config.name,
        enabled: nextEnabled,
        [recipientFieldName]: config[recipientFieldName]
      });
      await loadConfigs(selectedStore.id);
    } catch (err) {
      console.error(`Error toggling ${title} config:`, err);
      setError(err.response?.data?.error || err.response?.data?.message || `Failed to toggle ${title} configuration`);
    }
  };

  const startCreate = () => {
    setEditingConfig(null);
    setFormData(initialFormData);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const startEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name || '',
      enabled: config.enabled || false,
      [tokenFieldName]: '',
      [recipientFieldName]: config[recipientFieldName] || ''
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingConfig(null);
    setFormData(initialFormData);
    setError('');
  };

  const hasTokenFlag = (config) => config.hasToken || config.hasBotToken || false;

  if (loading) return <AdminLoading message={`Loading ${title} configurations...`} />;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Icon}
        title={title}
        subtitle={subtitle}
        rightContent={
          <div className="flex items-center gap-3">
            <select
              value={selectedStore?.id || ''}
              onChange={(e) => {
                const store = stores.find((s) => s.id === parseInt(e.target.value));
                setSelectedStore(store);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name || store.storeId}
                </option>
              ))}
            </select>
            <AdminAddButton onClick={startCreate} label={`Add ${title}`} />
          </div>
        }
      />

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <Check className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingConfig ? `Edit ${title}` : `Add New ${title}`}
          </h3>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  required
                />
              </div>
              <div className="flex items-center h-full pt-6">
                <input
                  id="enabled"
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enabled" className="ml-2 block text-sm text-gray-900">Enabled</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{tokenLabel}</label>
                <input
                  type="password"
                  value={formData[tokenFieldName]}
                  onChange={(e) => setFormData({ ...formData, [tokenFieldName]: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                />
                {editingConfig && hasTokenFlag(editingConfig) && (
                  <p className="mt-1 text-xs text-gray-500">Leave blank to keep the existing {tokenLabel.toLowerCase()}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{recipientLabel}</label>
                <input
                  type="text"
                  value={formData[recipientFieldName]}
                  onChange={(e) => setFormData({ ...formData, [recipientFieldName]: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelForm}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <X className="h-4 w-4 inline mr-1" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70 flex items-center"
              >
                {saving ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{recipientLabel}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enabled</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {configs.map((config) => (
              <tr key={config.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{config.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{config[recipientFieldName] || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button
                    onClick={() => handleToggleEnabled(config)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      config.enabled ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        config.enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <AdminRowActions>
                    <AdminActionButton variant="edit" onClick={() => startEdit(config)} />
                    <AdminActionButton variant="delete" onClick={() => handleDelete(config.id)} />
                  </AdminRowActions>
                </td>
              </tr>
            ))}
            {configs.length === 0 && (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                  No {title} configurations for this store. Add one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {confirmDialog}
    </div>
  );
};

export default ChannelConfigsManagement;
